'use client'
import { PropsWithChildren, useEffect } from 'react'
import { track } from '@vercel/analytics'
import { useTracking } from '@/hooks/use-tracking'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useTrackingStore } from '@/hooks/use-tracking/store'
import {
  currentStepSelector,
  qParamsSelector,
} from '@/hooks/use-tracking/selectors'
import { customerSelector } from '@/hooks/use-customer/selectors'
import { useCustomerStore } from '@/hooks/use-customer/store'
import { stepFinished } from '@/actions/adquisition'
import { useProductStore } from '@/hooks/use-cart-product/store'
import { productFieldsSelector } from '@/hooks/use-cart-product/selectors'
import capture from '@repo/tracking/posthog'
import { TrackEvent } from '@repo/tracking/types/TrackEvents'
import {
  recallSalesChatRef,
  rememberSalesChatRef,
  withoutSalesChatRef,
} from '@/utils/sales-chat-ref'

export function TrackingComponent({ children }: PropsWithChildren) {
  const tracking = useTracking()
  const params = useSearchParams()
  const { qParams, setQueryParams } = useTrackingStore(qParamsSelector)
  const { currentStep, setCurrentStep } = useTrackingStore(currentStepSelector)
  const router = useRouter()
  const path = usePathname()
  const customerStore = useCustomerStore(customerSelector)
  const productStore = useProductStore(productFieldsSelector)

  const pathname = usePathname()
  useEffect(() => {
    if (params.size > 0) {
      const { adi, ...rest } = getListFromParams(params)
      setCurrentStep({ ...currentStep, adi })
      // El `ref` del bot va a sessionStorage, nunca a localStorage (ver `QueryParam`).
      const qParamsFromUrl = rest as Record<string, string>
      rememberSalesChatRef(qParamsFromUrl.ref)
      setQueryParams(withoutSalesChatRef(qParamsFromUrl))
      router.replace(path)
    } else {
      const pathObject = getFunnel(pathname)
      setCurrentStep({ ...currentStep, ...pathObject })

      if (customerStore?.email) {
        stepFinished({
          step: pathObject?.funnel,
          currentUrl: `${window.location.origin}${pathname}`,
          sessionId: currentStep?.sessionId || 0,
          user: {
            email: customerStore?.email,
            name: customerStore?.name,
            personalNumber: customerStore?.dninie,
            phone: customerStore?.phone,
            lastname: customerStore?.lastName,
          },
          product: {
            academy: pathObject?.academy,
            plan: pathObject?.product,
            upselling: !!productStore?.selectedUpSelling,
            promotionCode: productStore?.promotion?.code || undefined,
            amount: productStore?.totalAmountWithDiscount,
          },
          tracking: {
            utm_source: qParams?.utm_source,
            utm_campaign: qParams?.utm_campaign,
            utm_medium: qParams?.utm_medium,
            utm_content: qParams?.utm_content,
            utm_term: qParams?.utm_term,
            // `ref` del asistente de ventas (`?ref=sc_…` del deep-link del bot),
            // de sessionStorage (solo esta sesión de compra): el API lo guarda en
            // `checkout.sales_chat_ref` y con él atribuye la compra a la
            // conversación al confirmar el pago.
            sales_chat_ref: recallSalesChatRef() || undefined,
          },
        }).then((res) =>
          setCurrentStep({
            sessionId: res?.id,
            adi: currentStep?.adi,
            ...pathObject,
          })
        )
        capture(TrackEvent.STEP_FINISHED, {
          email: customerStore?.email,
          name: customerStore?.name,
          personalNumber: customerStore?.dninie,
          phone: customerStore?.phone,
          lastname: customerStore?.lastName,
          product: {
            academy: pathObject?.academy,
            plan: pathObject?.product,
            upselling: !!productStore?.selectedUpSelling,
            promotionCode: productStore?.promotion?.code || undefined,
            amount: productStore?.totalAmountWithDiscount,
          },
        })
        track('Step', {
          step: pathObject?.funnel,
          academy: pathObject?.academy,
          email: customerStore?.email,
          plan: pathObject?.product,
          amount: productStore?.totalAmountWithDiscount,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, params])

  return <>{children}</>
}
function getListFromParams(params: URLSearchParams) {
  const list = { adi: null }
  params.forEach((value, key) => {
    list[key] = value
  })
  return list
}

function getFunnel(path) {
  const paths = path.split('/').filter((segment) => segment !== '')
  if (paths?.length > 2) {
    return {
      route: paths[0],
      academy: paths[1],
      product: paths[2],
      funnel: paths[3] || null,
    }
  }
  return null
}
